/**
 * Pool Reports Distribution Owner Page
 *
 * Displays vessel-level distribution data for a specific owner with
 * date filtering, breakdown toggles, and CSV export functionality
 * UI matches Angular implementation exactly
 */

import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { Button, Toggle, Skeleton } from '@/components/ui';
import { useDistributionDates, useDistributionsByOwner } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { CSVService } from '@/utils/csvExport';
import { formatNumber } from '@/utils/formatters';
import type { Distribution, DistributionDate } from '@/types/poolReports.types';
import {
  POOL_REPORT_VESSEL_COL_HEADER,
  poolReportTheadDrilldownClass,
  poolReportTheadStickyClass,
  poolReportThClass,
} from '@/components/poolReports/poolReportTableChrome';

interface HeadingItem {
  type: string;
  value: string;
  index: number;
  isBasisYear: boolean | null;
  key: string;
}

export function PoolReportsDistributionOwnerPage() {
  const navigate = useNavigate();
  const { ownerId } = useParams<{ ownerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedReport, reportType, setSubTitle } = usePoolReportsStore();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const [selectedDate, setSelectedDate] = useState<string>(searchParams.get('date') || '');
  const [displayData, setDisplayData] = useState<Distribution[]>([]);
  const [headings, setHeadings] = useState<HeadingItem[]>([]);

  // Toggle states
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showPostYear, setShowPostYear] = useState(false);

  const fileId = searchParams.get('fileId');
  const poolId = searchParams.get('poolId');
  const dateParam = searchParams.get('date');
  const ownerName = searchParams.get('ownerName') || '';
  const numericFileId = fileId ? Number(fileId) : 0;
  const numericOwnerId = ownerId ? Number(ownerId) : 0;

  const { data: distributionDatesData, isLoading: isDatesLoading } = useDistributionDates(
    numericFileId,
    Boolean(fileId)
  );
  const distributionDates: DistributionDate[] = useMemo(
    () => distributionDatesData || [],
    [distributionDatesData]
  );

  const { data: distributionsData, isLoading: isDistributionsLoading } = useDistributionsByOwner(
    numericFileId,
    numericOwnerId,
    selectedDate,
    Boolean(fileId && ownerId && selectedDate)
  );
  const distributions: Distribution[] = useMemo(() => distributionsData || [], [distributionsData]);
  const loading = isDatesLoading || isDistributionsLoading;

  // Set subtitle for navigation
  useEffect(() => {
    setSubTitle({
      value: `Distributions Made Year to Date: ${ownerName}`,
      backLink: `/reports/${reportType}/distribution`,
      queryParams: {
        poolId: poolId || '',
        fileId: fileId || '',
      },
    });

    return () => {
      setSubTitle(null);
    };
  }, [ownerName, reportType, poolId, fileId, setSubTitle]);

  // Sync selected date with URL and available dates
  useEffect(() => {
    if (!fileId || distributionDates.length === 0) {
      return;
    }

    if (dateParam && distributionDates.some((d) => d.DistributionDateStr === dateParam)) {
      if (dateParam !== selectedDate) {
        setSelectedDate(dateParam);
      }
      return;
    }

    const firstDate = distributionDates[0].DistributionDateStr;
    if (firstDate && firstDate !== selectedDate) {
      setSelectedDate(firstDate);
      setSearchParams({
        poolId: poolId || '',
        fileId: fileId || '',
        date: firstDate,
        ownerName,
      });
    }
  }, [fileId, dateParam, distributionDates, selectedDate, poolId, ownerName, setSearchParams]);

  // Process headings and filter data when distributions change
  useEffect(() => {
    if (!distributions || distributions.length === 0) {
      setHeadings([]);
      setDisplayData([]);
      return;
    }

    // Get basis year from first row
    const basisYear = distributions[0].BasisYear;

    // Filter out excluded fields
    const excludeFields = ['basisyear', 'ownerid', 'vesselid', 'ownername'];
    const filtered = distributions.map((distribution: Distribution) => {
      const filtered: Distribution = {};
      Object.keys(distribution).forEach((key) => {
        if (!excludeFields.includes(key.toLowerCase())) {
          filtered[key] = distribution[key];
        }
      });
      return filtered;
    });

    // Build heading structure
    if (filtered.length > 0) {
      const headingItems: HeadingItem[] = Object.keys(filtered[0]).map((key, index) => {
        const parts = key.split('|');
        const displayValue =
          parts[0] === 'Name'
            ? POOL_REPORT_VESSEL_COL_HEADER
            : parts.length > 1
              ? parts[1]
              : parts[0];
        const type = parts.length > 1 ? parts[0] : '';

        let isBasisYear: boolean | null = null;

        // Determine if this column is for the basis year
        if (parts.length > 1 && ['current', 'post', 'breakdown'].includes(parts[0])) {
          const lastPart = parts[1].split(' ').pop() || '';
          const year = lastPart.replace(/[^0-9]/g, '');
          isBasisYear = Number(basisYear) === Number(year);
        }

        if (key.toLowerCase().indexOf('previous years') > -1) {
          isBasisYear = false;
        }

        return {
          type,
          value: displayValue,
          index,
          isBasisYear,
          key,
        };
      });

      setHeadings(headingItems);
      setDisplayData(filtered);
    }
  }, [distributions]);

  // Handle date selection change
  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setSearchParams({
      poolId: poolId || '',
      fileId: fileId || '',
      date: newDate,
      ownerName,
    });
  };

  // Handle showPostYear toggle - scroll to right when enabled
  const handlePostYearToggle = (value: boolean) => {
    setShowPostYear(value);

    if (value && tableWrapperRef.current) {
      setTimeout(() => {
        if (tableWrapperRef.current) {
          tableWrapperRef.current.scrollTo({
            behavior: 'smooth',
            left: tableWrapperRef.current.scrollWidth - tableWrapperRef.current.clientWidth,
          });
        }
      }, 50);
    }
  };

  const handleExportCSV = () => {
    if (displayData.length === 0 || headings.length === 0) return;

    // Filter headings based on toggles (like Angular does)
    const visibleHeadings = headings.filter(({ type }) => {
      if (!showBreakdown && type === 'breakdown') return false;
      if (!showPostYear && type === 'post') return false;
      return true;
    });

    const reportRows = displayData.map((item: Distribution) =>
      visibleHeadings.map((head, index) => {
        const value = item[head.key];
        if (value === null || value === undefined) return '';
        if (index > 0 && typeof value === 'number') return formatNumber(value, 0, 0);
        return String(value);
      })
    );

    const fileName = `${selectedReport?.description || 'report'}-distribution-report-vessels`;
    CSVService.convert(
      reportRows,
      visibleHeadings.map(({ value }) => value)
    ).download(fileName);
  };

  const handleVesselClick = (index: number) => {
    const vesselData = distributions?.[index];
    if (!vesselData?.VesselID) return; // Don't navigate for total row

    const route = `/reports/${reportType}/distribution/owner/${ownerId}/${vesselData.VesselID}`;
    navigate(
      `${route}?poolId=${poolId}&fileId=${fileId}&ownerName=${encodeURIComponent(ownerName)}&vesselName=${encodeURIComponent(vesselData.Name || '')}`
    );
  };

  const isTotal = (index: number) => {
    const vesselData = distributions?.[index];
    return !vesselData?.VesselID;
  };

  return (
    <div className="space-y-4">
      {/* Table - Angular Style */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
        {/* Table Header with Controls and Export Button */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-dark-700">
          <div className="flex flex-wrap items-center gap-4">
            <h2 className="type-section-title">Vessel Details</h2>

            {/* Date Selector */}
            <select
              value={selectedDate}
              onChange={handleDateChange}
              disabled={loading || !distributionDates || distributionDates.length === 0}
              className="min-w-[180px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:focus:border-primary-400"
            >
              <option value="" disabled>
                Distribution From
              </option>
              {distributionDates?.map((date) => (
                <option key={date.DistributionDateStr} value={date.DistributionDateStr}>
                  {date.DisplayDate}
                </option>
              ))}
            </select>

            {/* Breakdown By Year Toggle */}
            <div className="flex items-center gap-2">
              <Toggle
                id="showBreakdown"
                checked={showBreakdown}
                onChange={setShowBreakdown}
                disabled={loading}
              />
              <label
                htmlFor="showBreakdown"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Breakdown By Year
              </label>
            </div>

            {/* Post Year Distribution Toggle */}
            <div className="flex items-center gap-2">
              <Toggle
                id="showPostYear"
                checked={showPostYear}
                onChange={handlePostYearToggle}
                disabled={loading}
              />
              <label
                htmlFor="showPostYear"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Post Year Distribution
              </label>
            </div>
          </div>

          {/* Export Button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={loading || displayData.length === 0}
            className="rounded-full px-4 shadow-sm"
          >
            Export to Excel
          </Button>
        </div>

        <div ref={tableWrapperRef} className="report-table-shell">
          <table
            className={`distribution-table report-table ${showBreakdown ? 'show-breakdown' : ''} ${showPostYear ? 'show-post-year' : ''}`}
          >
            {loading ? (
              <>
                {/* Loading skeleton */}
                <thead className={poolReportTheadDrilldownClass}>
                  <tr>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <th key={i} className={poolReportThClass}>
                        <Skeleton className="ml-auto h-4 w-20" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-900">
                  {Array.from({ length: 7 }).map((_, rowIdx) => (
                    <tr key={rowIdx} className="border-t border-gray-200 dark:border-dark-700">
                      {Array.from({ length: 12 }).map((_, colIdx) => (
                        <td key={colIdx} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                {/* Table Header */}
                {headings.length > 0 && (
                  <thead className={poolReportTheadStickyClass}>
                    <tr>
                      {headings.map((heading) => (
                        <th
                          key={heading.key}
                          className={clsx(
                            poolReportThClass,
                            heading.type,
                            heading.isBasisYear === true
                              ? 'bg-primary-100 dark:bg-primary-900/20'
                              : heading.isBasisYear === false
                                ? 'bg-gray-100 dark:bg-dark-700'
                                : ''
                          )}
                        >
                          {heading.value}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}

                {/* Table Body */}
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {displayData.map((row, rowIdx) => {
                    const isTotalRow = isTotal(rowIdx);

                    return (
                      <tr key={rowIdx} className="hover:bg-gray-50 dark:hover:bg-dark-800">
                        {headings.map((heading, colIdx) => {
                          const value = row[heading.key];
                          const isVesselColumn = colIdx === 0;

                          return (
                            <td
                              key={heading.key}
                              className={clsx(
                                'px-2.5 py-2 text-right text-sm sm:px-3 sm:py-2.5',
                                !isVesselColumn && 'tabular-nums',
                                heading.type,
                                heading.isBasisYear === true
                                  ? 'bg-primary-50/50 dark:bg-primary-900/10'
                                  : heading.isBasisYear === false
                                    ? 'bg-gray-50 dark:bg-dark-800/50'
                                    : ''
                              )}
                            >
                              {isVesselColumn && value ? (
                                isTotalRow ? (
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {value}
                                  </span>
                                ) : (
                                  <div
                                    className="flex cursor-pointer items-center justify-end gap-2"
                                    onClick={() => handleVesselClick(rowIdx)}
                                  >
                                    <span className="text-md font-semibold uppercase text-gray-900 underline hover:no-underline dark:text-white">
                                      {value}
                                    </span>
                                    <ExternalLink className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400" />
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-900 dark:text-white">
                                  {typeof value === 'number'
                                    ? formatNumber(value, 0, 0)
                                    : value || ''}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>

      {/* No Data State */}
      {!loading && displayData.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No distribution data available</p>
        </div>
      )}
    </div>
  );
}
